if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',

    class ProductInfo extends HTMLElement {
      constructor() {
        super();

        this.input = this.querySelector('.quantity__input');
        this.currentVariant = this.querySelector('.product-variant-id');
        this.submitButton = this.querySelector('[type="submit"]');
        this.sectionId = this.dataset.originalSection || this.dataset.section;
        this.recallCounter = 0;
        this.optionSize = parseInt(this.dataset.optionSize);

        this.preProcessHtmlCallbacks = [];
        this.postProcessHtmlCallbacks = [];

        // Bereits geladene Varianten zwischenspeichern.
        this.variantResponseCache = new Map();
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.lastKnownVariant = this.getSelectedVariant(this);

        if (this.input) {
          this.quantityForm = this.querySelector(
            '.product-form__quantity-wrapper'
          );

          if (this.quantityForm) {
            this.setQuantityBoundries();

            this.cartUpdateHandler =
              this.fetchQuantityRules.bind(this);

            document.addEventListener(
              'afterCartChanged',
              this.cartUpdateHandler
            );

            document.addEventListener(
              'afterUpdateQuantity',
              this.cartUpdateHandler
            );
          }
        }

        this.optionValueChangeHandler =
          this.handleOptionValueChange.bind(this);

        document.addEventListener(
          'optionValueSelectionChange',
          this.optionValueChangeHandler
        );
      }

      disconnectedCallback() {
        if (this.cartUpdateHandler) {
          document.removeEventListener(
            'afterCartChanged',
            this.cartUpdateHandler
          );

          document.removeEventListener(
            'afterUpdateQuantity',
            this.cartUpdateHandler
          );
        }

        if (this.optionValueChangeHandler) {
          document.removeEventListener(
            'optionValueSelectionChange',
            this.optionValueChangeHandler
          );
        }
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initializeProductSwapUtility() {
        this.postProcessHtmlCallbacks.push(() => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange(event) {
        if (
          event.detail.sectionId !== this.dataset.section ||
          (
            event.detail.recallId !== undefined &&
            event.detail.recallId !== this.dataset.section
          )
        ) {
          return;
        }

        this.resetProductFormState();

        const productUrl =
          event.detail.target.dataset.productUrl ||
          this.pendingRequestUrl ||
          this.dataset.url;

        this.pendingRequestUrl = productUrl;

        const shouldSwapProduct =
          this.dataset.url !== productUrl;

        if (
          shouldSwapProduct &&
          this.dataset.hasOutsideElements
        ) {
          document.dispatchEvent(
            new CustomEvent('swapProductOutsideNode', {
              detail: {
                oldSectionId: this.dataset.section,
                productUrl: productUrl,
                index: this.dataset.index
              }
            })
          );

          return;
        }

        const shouldFetchFullPage =
          this.dataset.updateUrl === 'true' &&
          shouldSwapProduct &&
          !this.dataset.inSticky;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(
            productUrl,
            event.detail.selectedOptionValues,
            shouldFetchFullPage
          ),

          targetId: event.detail.target.id,

          callback: shouldSwapProduct
            ? this.handleSwapProduct(
                productUrl,
                shouldFetchFullPage
              )
            : this.handleUpdateProductInfo(productUrl)
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;

        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage
            ? "product-info[id^='MainProduct']"
            : (
                this.id
                  ? `#${this.id}`
                  : 'product-info'
              );

          const variant = this.getSelectedVariant(
            html.querySelector(selector)
          );

          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector(
              'head title'
            ).innerHTML =
              html.querySelector(
                'head title'
              ).innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector(selector),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }

          if (this.dataset.inSticky) {
            setTimeout(() => {
              document
                .querySelector(
                  "product-info[id^='MainProduct']"
                )
                .scrollIntoView({
                  behavior: 'smooth'
                });
            }, 500);
          }
        };
      }

      renderProductInfo({
        requestUrl,
        targetId,
        callback
      }) {
        /*
         * Falls diese Geschmacks-/Variantenkombination bereits
         * geladen wurde, direkt aus dem Speicher verwenden.
         */
        const cachedResponse =
          this.variantResponseCache.get(requestUrl);

        if (cachedResponse) {
          this.pendingRequestUrl = null;

          const html =
            new DOMParser().parseFromString(
              cachedResponse,
              'text/html'
            );

          callback(html);

          requestAnimationFrame(() => {
            document
              .querySelector(`#${targetId}`)
              ?.focus();
          });

          return;
        }

        /*
         * Alte Anfrage abbrechen, wenn der Kunde schnell
         * mehrere Geschmäcker hintereinander anklickt.
         */
        this.abortController?.abort();

        this.abortController =
          new AbortController();

        fetch(requestUrl, {
          signal: this.abortController.signal,
          credentials: 'same-origin'
        })
          .then((response) => {
            if (!response.ok) {
              throw new Error(
                `HTTP ${response.status}`
              );
            }

            return response.text();
          })

          .then((responseText) => {
            this.pendingRequestUrl = null;

            /*
             * Shopify-Antwort speichern.
             * Beim nächsten Wechsel zu genau dieser Variante
             * ist kein neuer Server-Request erforderlich.
             */
            this.variantResponseCache.set(
              requestUrl,
              responseText
            );

            const html =
              new DOMParser().parseFromString(
                responseText,
                'text/html'
              );

            callback(html);
          })

          .then(() => {
            requestAnimationFrame(() => {
              document
                .querySelector(`#${targetId}`)
                ?.focus();
            });
          })

          .catch((error) => {
            this.recallCounter = 0;

            if (error.name === 'AbortError') {
              return;
            }

            console.error(error);
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant =
          productInfoNode
            ?.querySelector(
              '[data-selected-variant]'
            )
            ?.innerHTML;

        return selectedVariant
          ? JSON.parse(selectedVariant)
          : null;
      }

      buildRequestUrlWithParams(
        url,
        optionValues,
        shouldFetchFullPage = false
      ) {
        const params = [];

        if (!shouldFetchFullPage) {
          params.push(
            `section_id=${this.sectionId}`
          );
        }

        if (optionValues.length) {
          params.push(
            `option_values=${optionValues.join(',')}`
          );
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects =
          html.getElementById(
            this.variantSelectors.id
          );

        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(
            this.variantSelectors,
            variantSelects,
            this.preProcessHtmlCallbacks
          );
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant =
            this.getSelectedVariant(html);

          if (
            !variant &&
            window.hideUnavailableOptions &&
            this.recallCounter <
              (this.optionSize - 1)
          ) {
            this.recallCounter++;

            this.variantSelectors
              .autoCorrectAvailableVariant(
                html.getElementById(
                  `ProductInfo-${this.dataset.section}`
                )
              );

            return true;
          }

          this.recallCounter = 0;

          this.updateURL(
            productUrl,
            variant?.id
          );

          this.updateElements(
            variant,
            html
          );

          if (this.dataset.syncElement) {
            const syncElement =
              document.getElementById(
                this.dataset.syncElement
              );

            syncElement?.updateElements(
              variant,
              html
            );
          }
        };
      }

      swapElementsOutsideProductNode(html) {
        const variant =
          this.getSelectedVariant(html);

        this.updateElements(
          variant,
          html,
          true
        );
      }

      updateElements(variant, html) {
        const previousVariant =
          this.lastKnownVariant;

        this.updateOptionValues(html);

        this.pickupAvailability?.update(
          variant
        );

        this.updateVariantInputs(
          variant?.id
        );

        if (!variant) {
          this.lastKnownVariant = null;

          this.setUnavailable();

          return;
        }

        this.updateMedia(
          variant,
          html,
          previousVariant
        );

        const descAddButton =
          document.querySelector(
            `#product-form-${this.dataset.section} [name="add"]`
          );

        const sourceAddButton =
          html.querySelector(
            `#product-form-${this.dataset.section} [name="add"]`
          );

        if (
          sourceAddButton &&
          descAddButton
        ) {
          this.productForm
            ?.toggleSubmitButton(
              !variant ||
                !variant.available,
              window.variantStrings.soldOut
            );

          const descAddButtonText =
            descAddButton.querySelector(
              '[name="add"] > span'
            );

          if (descAddButtonText) {
            if (
              sourceAddButton.dataset.preOrder
            ) {
              descAddButton.dataset.preOrder =
                sourceAddButton.dataset.preOrder;

              const sourceText =
                sourceAddButton.querySelector(
                  '[name="add"] > span'
                );

              if (sourceText) {
                descAddButtonText.textContent =
                  sourceText.textContent;
              }
            } else {
              descAddButton.removeAttribute(
                'data-pre-order'
              );

              if (
                variant &&
                variant.available
              ) {
                const sourceText =
                  sourceAddButton.querySelector(
                    '[name="add"] > span'
                  );

                if (sourceText) {
                  descAddButtonText.textContent =
                    sourceText.textContent;
                }
              }
            }
          }
        }

        this.updateSourceFromDestination(
          html,
          'Volume'
        );

        this.updateSourceFromDestination(
          html,
          'Volume-Note'
        );

        this.updateSourceFromDestination(
          html,
          'Featured-Media'
        );

        this.updateSourceFromDestination(
          html,
          'price'
        );

        this.updateSourceFromDestination(
          html,
          'price-top'
        );

        this.updateSourceFromDestination(
          html,
          'ProductAddOns'
        );

        const inventoryDestination =
          document.querySelectorAll(
            `.inventory-${this.dataset.section}`
          );

        const inventorySource =
          html.querySelector(
            `.inventory-${this.dataset.section}`
          );

        if (inventorySource) {
          inventoryDestination.forEach(
            (invDesc) => {
              invDesc.innerHTML =
                inventorySource.innerHTML;
            }
          );
        }

        const skuDestination =
          document.querySelectorAll(
            `.sku-${this.dataset.section}`
          );

        const skuSource =
          html.querySelector(
            `.sku-${this.dataset.section}`
          );

        if (skuSource) {
          skuDestination.forEach(
            (skuDesc) => {
              skuDesc.innerHTML =
                skuSource.innerHTML;

              skuDesc.parentElement
                ?.classList.toggle(
                  'hidden',
                  skuSource.parentElement
                    ?.classList.contains(
                      'hidden'
                    )
                );
            }
          );
        }

        const barcodeDestination =
          document.querySelectorAll(
            `.barcode-${this.dataset.section}`
          );

        const barcodeSource =
          html.querySelector(
            `.barcode-${this.dataset.section}`
          );

        if (barcodeSource) {
          barcodeDestination.forEach(
            (barcodeDesc) => {
              barcodeDesc.innerHTML =
                barcodeSource.innerHTML;

              barcodeDesc.parentElement
                ?.classList.toggle(
                  'hidden',
                  barcodeSource.parentElement
                    ?.classList.contains(
                      'hidden'
                    )
                );
            }
          );
        }

        this.updateQuantityRules(
          this.dataset.section,
          html
        );

        this.setQuantityBoundries();

        this.querySelector(
          `#Quantity-Rules-${this.dataset.section}`
        )?.classList.remove('hidden');

        this.querySelector(
          `#Volume-Note-${this.dataset.section}`
        )?.classList.remove('hidden');

        this.lastKnownVariant = variant;

        document.dispatchEvent(
          new CustomEvent(
            'afterVariantChanged',
            {
              detail: {
                sectionId:
                  this.dataset.section,

                html,

                variant
              }
            }
          )
        );
      }

      updateSourceFromDestination(
        html,
        id
      ) {
        const source =
          html.getElementById(
            `${id}-${this.dataset.section}`
          );

        const destination =
          document.getElementById(
            `${id}-${this.dataset.section}`
          );

        if (
          source &&
          destination
        ) {
          destination.innerHTML =
            source.innerHTML;

          destination.classList.remove(
            'hidden'
          );
        }
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input =
            productForm.querySelector(
              'input[name="id"]'
            );

          if (!input) {
            return;
          }

          input.value =
            variantId ?? '';

          input.dispatchEvent(
            new Event('change', {
              bubbles: true
            })
          );
        });

        this.currentVariant =
          this.querySelector(
            '.product-variant-id'
          );
      }

      updateURL(url, variantId) {
        this.querySelector(
          'share-button'
        )?.updateUrl(
          `${window.shopUrl}${url}${
            variantId
              ? `?variant=${variantId}`
              : ''
          }`
        );

        if (
          this.dataset.updateUrl === 'false'
        ) {
          return;
        }

        window.history.replaceState(
          {},
          '',
          `${url}${
            variantId
              ? `?variant=${variantId}`
              : ''
          }`
        );
      }

      setUnavailable() {
        this.productForm
          ?.toggleSubmitButton(
            true,
            window.variantStrings.unavailable
          );

        const selectors = [
          'price',
          'price-top',
          'Inventory',
          'Volume-Note',
          'Volume',
          'Quantity-Rules'
        ]
          .map(
            (id) =>
              `#${id}-${this.dataset.section}`
          )
          .join(', ');

        document
          .querySelectorAll(selectors)
          .forEach(
            ({ classList }) =>
              classList.add('hidden')
          );
      }

      updateMedia(
        variant,
        html,
        previousVariant
      ) {
        if (!variant || !html) {
          return;
        }

        /*
         * Die von Shopify geladene Section enthält bereits
         * die Galerie für den aktuell ausgewählten Geschmack.
         *
         * Deshalb wird sie bei jedem Variantenwechsel
         * direkt synchronisiert.
         */
        const sourceMediaGallery =
          html.querySelector(
            `#MediaGallery-${this.dataset.section}`
          );

        if (!sourceMediaGallery) {
          return;
        }

        const mediaGalleries =
          document.querySelectorAll(
            `[id^="MediaGallery-${this.dataset.section}"]`
          );

        /*
         * Das aktive Bild aus der neu geladenen
         * Galerie bestimmen.
         */
        const sourceActiveMedia =
          sourceMediaGallery.querySelector(
            '[data-media-id].is-active'
          ) ||
          sourceMediaGallery.querySelector(
            '.is-active[data-media-id]'
          ) ||
          sourceMediaGallery.querySelector(
            '[data-media-id]'
          );

        const sourceActiveMediaId =
          sourceActiveMedia
            ?.dataset
            ?.mediaId ||
          null;

        mediaGalleries.forEach(
          (mediaGallery) => {
            /*
             * Große Bilder + Thumbnails mit
             * der neuen Galerie ersetzen.
             */
            if (
              typeof mediaGallery
                .syncFromHtml ===
              'function'
            ) {
              mediaGallery.syncFromHtml(
                sourceMediaGallery
              );
            }

            let targetMediaId =
              sourceActiveMediaId;

            /*
             * Falls serverseitig kein aktives Bild
             * markiert wurde, Featured Media verwenden.
             */
            if (
              !targetMediaId &&
              variant.featured_media?.id
            ) {
              targetMediaId =
                `${this.dataset.section}-${variant.featured_media.id}`;
            }

            if (
              targetMediaId &&
              typeof mediaGallery
                .setActiveMedia ===
                'function'
            ) {
              const targetExists =
                mediaGallery.querySelector(
                  `[data-media-id="${targetMediaId}"]`
                );

              const fallbackMediaId =
                mediaGallery.querySelector(
                  '[data-media-id]'
                )?.dataset?.mediaId;

              const finalMediaId =
                targetExists
                  ? targetMediaId
                  : fallbackMediaId;

              if (finalMediaId) {
                /*
                 * prepend = false:
                 * Reihenfolge der Galerie nicht verändern.
                 *
                 * allowScrollIntoView = false:
                 * Produktseite nicht beim Bildwechsel
                 * nach oben/unten springen lassen.
                 */
                mediaGallery.setActiveMedia(
                  finalMediaId,
                  false,
                  false
                );
              }
            }
          }
        );

        /*
         * Lightbox / Modal ebenfalls auf
         * die gewählte Variante ausrichten.
         */
        if (
          variant.featured_media?.id
        ) {
          const modalContent =
            document.querySelector(
              `#ProductModal-${this.dataset.section} .product-media-modal__content`
            );

          if (modalContent) {
            const newMediaModal =
              modalContent.querySelector(
                `[data-media-id="${variant.featured_media.id}"]`
              );

            if (newMediaModal) {
              modalContent.prepend(
                newMediaModal
              );
            }
          }
        }
      }

      setQuantityBoundries() {
        if (!this.input) {
          return;
        }

        const data = {
          cartQuantity:
            this.input.dataset
              .cartQuantity
              ? parseInt(
                  this.input.dataset
                    .cartQuantity
                )
              : 0,

          min:
            this.input.dataset.min
              ? parseInt(
                  this.input.dataset.min
                )
              : 1,

          max:
            this.input.dataset.max
              ? parseInt(
                  this.input.dataset.max
                )
              : null,

          step:
            this.input.step
              ? parseInt(
                  this.input.step
                )
              : 1
        };

        let min = data.min;

        const max =
          data.max === null
            ? data.max
            : data.max -
              data.cartQuantity;

        if (max !== null) {
          min = Math.min(
            min,
            max
          );
        }

        if (
          data.cartQuantity >=
          data.min
        ) {
          min = Math.min(
            min,
            data.step
          );
        }

        this.input.min = min;

        if (max) {
          this.input.max = max;
        } else {
          this.input.removeAttribute(
            'max'
          );
        }

        this.input.value = min;

        document.dispatchEvent(
          new CustomEvent(
            'afterQuantityUpdate'
          )
        );
      }

      fetchQuantityRules() {
        if (
          !this.currentVariant ||
          !this.currentVariant.value
        ) {
          return;
        }

        const quantityLabelLoading =
          this.querySelector(
            '.quantity__label__loading'
          );

        quantityLabelLoading
          ?.classList.remove(
            'hidden'
          );

        fetch(
          `${this.dataset.url}?variant=${this.currentVariant.value}&section_id=${this.sectionId}`
        )
          .then(
            (response) =>
              response.text()
          )

          .then(
            (responseText) => {
              const html =
                new DOMParser()
                  .parseFromString(
                    responseText,
                    'text/html'
                  );

              this.updateQuantityRules(
                this.dataset.section,
                html
              );

              this.setQuantityBoundries();
            }
          )

          .catch((error) => {
            console.error(error);
          })

          .finally(() => {
            this.querySelector(
              '.quantity__label__loading'
            )?.classList.add(
              'hidden'
            );
          });
      }

      updateQuantityRules(
        sectionId,
        html
      ) {
        if (!this.quantityForm) {
          return;
        }

        const quantityFormUpdated =
          html.getElementById(
            `Quantity-Form-${sectionId}`
          );

        if (!quantityFormUpdated) {
          return;
        }

        const selectors = [
          '.quantity__input',
          '.quantity__rules',
          '.quantity__label'
        ];

        if (
          quantityFormUpdated
            .classList.contains(
              'product-form__wholesale'
            )
        ) {
          if (
            !this.quantityForm
              .classList.contains(
                'product-form__wholesale'
              )
          ) {
            this.quantityForm
              .classList.add(
                'product-form__wholesale'
              );
          }
        } else if (
          this.quantityForm
            .classList.contains(
              'product-form__wholesale'
            )
        ) {
          this.quantityForm
            .classList.remove(
              'product-form__wholesale'
            );
        }

        for (
          const selector of selectors
        ) {
          const current =
            this.quantityForm
              .querySelector(selector);

          const updated =
            quantityFormUpdated
              .querySelector(selector);

          if (
            !current ||
            !updated
          ) {
            continue;
          }

          if (
            selector ===
            '.quantity__input'
          ) {
            const attributes = [
              'data-cart-quantity',
              'data-min',
              'data-max',
              'step'
            ];

            for (
              const attribute of attributes
            ) {
              const valueUpdated =
                updated.getAttribute(
                  attribute
                );

              if (
                valueUpdated !== null
              ) {
                current.setAttribute(
                  attribute,
                  valueUpdated
                );
              } else {
                current.removeAttribute(
                  attribute
                );
              }
            }
          } else {
            current.innerHTML =
              updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(
          'product-form'
        );
      }

      get variantSelectors() {
        return (
          this.querySelector(
            'variant-selects,variant-radios'
          ) ||
          document.getElementById(
            `variant-select-${this.dataset.section}`
          )
        );
      }

      get pickupAvailability() {
        return this.querySelector(
          'pickup-availability'
        );
      }
    }
  );
}